import { HHBox, HHChip, HHStack } from '@hinge-health/react-component-library';
import { FileParsingBatchStatus } from '../domain/enums';
import { BatchTotals } from '../domain/types';
import { formatCount } from '../utils/format.utils';
import { BATCH_CELL_COLORS } from './batch-heatmap';

export const BATCH_TOTALS_BAR_TEST_ID = 'pdg-batch-totals-bar';
export const BATCH_TOTAL_TEST_ID_PREFIX = 'pdg-batch-total';

export const batchTotalTestId = (key: keyof BatchTotals): string =>
  `${BATCH_TOTAL_TEST_ID_PREFIX}-${key}`;

interface BatchTotalConfig {
  key: keyof BatchTotals;
  label: string;
  /** Omitted for the roll-up count, which is not a status. */
  status?: FileParsingBatchStatus;
}

/**
 * Ordered so the counts an operator cares about most (what finished, what
 * broke) sit next to the total.
 */
const BATCH_TOTAL_CONFIGS: BatchTotalConfig[] = [
  { key: 'total', label: 'Total' },
  {
    key: 'completed',
    label: 'Completed',
    status: FileParsingBatchStatus.Completed,
  },
  { key: 'failed', label: 'Failed', status: FileParsingBatchStatus.Failed },
  {
    key: 'inProgress',
    label: 'In progress',
    status: FileParsingBatchStatus.Processing,
  },
  { key: 'pending', label: 'Pending', status: FileParsingBatchStatus.Pending },
  { key: 'skipped', label: 'Skipped', status: FileParsingBatchStatus.Skipped },
];

const DOT_SIZE_PX = 8;

export interface BatchTotalsBarProps {
  totals: BatchTotals;
}

/**
 * Whole-file batch counts for the parsing tab.
 *
 * Each chip carries a dot in the same colour the heatmap uses for that
 * status, so the bar also reads as the heatmap's legend. The colour is never
 * the only cue — every chip is labelled.
 */
export const BatchTotalsBar = ({
  totals,
}: BatchTotalsBarProps): JSX.Element => (
  <HHStack
    direction="row"
    gap={1}
    flexWrap="wrap"
    data-testid={BATCH_TOTALS_BAR_TEST_ID}
  >
    {BATCH_TOTAL_CONFIGS.map(({ key, label, status }) => (
      <HHChip
        key={key}
        hhVariant="variant-bypass"
        variant="outlined"
        size="small"
        data-testid={batchTotalTestId(key)}
        icon={
          status && (
            <HHBox
              sx={{
                width: DOT_SIZE_PX,
                height: DOT_SIZE_PX,
                borderRadius: '50%',
                backgroundColor: BATCH_CELL_COLORS[status],
              }}
            />
          )
        }
        label={`${label} ${formatCount(totals[key])}`}
      />
    ))}
  </HHStack>
);
